'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { SERVICE_GALLERY_IMAGE_SIZES } from '../../constants/gallery-image-sizes';
import { Heading } from '../ui/heading';
import { Carousel } from '../ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { useState } from 'react';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { Button } from '../ui/button';
import { IoSearchOutline } from 'react-icons/io5';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ServiceGalleryCarousel({ references }: { references: Reference[] }) {
  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex, slideCount } = carouselContext;

  const progress = ((selectedIndex + 1) / slideCount) * 100;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 py-8 pl-4 pr-0 sm:gap-10 sm:py-10 md:py-14 lg:gap-12 lg:py-16 xl:py-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-4 md:flex-row md:items-end md:justify-between',
        )}
      >
        <Heading
          title={t('text1763')}
          subtitle={t('text2246')}
          className={{ container: 'w-full max-w-[550px] items-start justify-start text-start' }}
        />
        <CarouselControls
          buttonProps={{ variant: 'primary-foreground', size: 'icon' }}
          className={{
            main: 'hidden gap-4 sm:flex',
            button: cn(
              'border-x-0 border-b-2 border-t-0 border-b-primary bg-primary-foreground text-black hover:bg-primary-foreground/90',
              'ea-services__carousel-control-button',
            ),
          }}
        />
      </div>
      <div className={cn('custom-container', 'relative')}>
        <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
        <Carousel
          options={options}
          className={{
            container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
            main: 'w-full overflow-visible',
            wrapper: 'w-full overflow-visible',
            slide: 'w-full max-w-[260px] sm:max-w-[380px] md:max-w-[490px] lg:max-w-[588px]',
          }}
          slides={references.map(reference => (
            <ServiceGalleryCard reference={reference} key={reference.id} />
          ))}
        />
      </div>
      <div className={cn('custom-container', 'pr-4 sm:pr-0')}>
        <div className="flex h-[2px] w-full items-center justify-start bg-black/10 sm:hidden">
          <div
            className={cn(
              'h-1 bg-black transition-all duration-300',
              'ea-team-members__carousel-progress-bar-indicator',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </section>
  );
}

export function ServiceGalleryCard({ reference }: { reference: Reference }) {
  const { t } = useTranslations();
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = [reference.image, ...(reference.images || [])];

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        className={cn(
          'relative h-[195px] w-full max-w-[260px] overflow-hidden sm:h-[230px] sm:max-w-[420px] md:h-[330px] md:max-w-[520px] lg:h-[400px] lg:max-w-[588px] xl:h-[440px]',
          'group transition-all',
        )}
      >
        {reference?.images && reference.images.length > 0 && (
          <Button
            aria-label={`${t('text1944')}: ${reference.name}`}
            onClick={handleOpenGallery(allImages, 0)}
            variant={'secondary'}
            size={'icon'}
            className="absolute bottom-4 left-4 z-10 cursor-pointer sm:bottom-6 sm:left-6 lg:bottom-8 lg:left-8"
          >
            <IoSearchOutline className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        )}
        <div
          className={cn(
            'absolute inset-0 z-[1] h-full w-full bg-black/30 opacity-0',
            'transition-all group-hover:opacity-100',
          )}
        />
        <Image
          src={reference.image.url}
          alt={resolveImageAlt(reference?.image, reference?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes={SERVICE_GALLERY_IMAGE_SIZES}
        />
      </div>
    </>
  );
}
